import React, { useEffect, useRef, useState } from 'react';
import {
  placesService,
  newSessionToken,
  type AddressDetails,
  type AddressSuggestion,
} from '../services/places.service';
import './AddressAutocomplete.css';

interface Props {
  value: string;
  onChange: (adresse: string) => void;
  /** Appelé quand une suggestion est retenue : adresse, CP, ville, pays ET
   *  coordonnées arrivent ensemble — c'est ce qui rend le géocodage inutile. */
  onResolved: (details: AddressDetails) => void;
  id?: string;
  placeholder?: string;
  required?: boolean;
}

/**
 * Saisie d'adresse assistée par Google Places, via le proxy de notre API.
 *
 * Dégradation volontaire : si la recherche échoue — service indisponible, clé
 * absente, hors ligne — le champ reste une saisie libre parfaitement
 * utilisable. On perd les coordonnées, pas la possibilité de travailler.
 */
const AddressAutocomplete: React.FC<Props> = ({
  value, onChange, onResolved, id, placeholder, required,
}) => {
  const [suggestions, setSuggestions] = useState<AddressSuggestion[]>([]);
  const [ouvert, setOuvert] = useState(false);
  const [indisponible, setIndisponible] = useState(false);
  const [resolue, setResolue] = useState(false);
  const session = useRef(newSessionToken());
  const conteneur = useRef<HTMLDivElement>(null);
  // Ignore la réponse d'une frappe dépassée par une plus récente.
  const derniereRequete = useRef(0);
  // La fiche Détails arrive avec une adresse déjà remplie. Sans ce garde, la
  // liste s'ouvrirait toute seule au chargement — et chaque ouverture de page
  // déclencherait une requête facturée. On ne cherche qu'après une frappe.
  const saisieUtilisateur = useRef(false);

  useEffect(() => {
    if (!saisieUtilisateur.current || value.trim().length < 3 || resolue) {
      setSuggestions([]);
      return;
    }
    const requete = ++derniereRequete.current;
    // 300 ms : sans ce délai, chaque caractère partirait chez Google.
    const t = setTimeout(async () => {
      try {
        const res = await placesService.autocomplete(value, session.current);
        if (requete !== derniereRequete.current) return;
        setSuggestions(res);
        setOuvert(res.length > 0);
        setIndisponible(false);
      } catch {
        if (requete !== derniereRequete.current) return;
        setSuggestions([]);
        setIndisponible(true);
      }
    }, 300);
    return () => clearTimeout(t);
  }, [value, resolue]);

  // Fermeture au clic extérieur.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (conteneur.current && !conteneur.current.contains(e.target as Node)) {
        setOuvert(false);
      }
    };
    document.addEventListener('mousedown', onClick);
    return () => document.removeEventListener('mousedown', onClick);
  }, []);

  const choisir = async (s: AddressSuggestion) => {
    setOuvert(false);
    setResolue(true);
    try {
      const details = await placesService.details(s.placeId, session.current);
      onResolved(details);
    } catch {
      // On garde au moins le texte choisi : mieux qu'un champ vidé.
      onChange(s.description);
      setIndisponible(true);
    } finally {
      // Nouvelle session : la précédente est consommée par la sélection.
      session.current = newSessionToken();
    }
  };

  return (
    <div className="adresse-auto" ref={conteneur}>
      <input
        id={id}
        type="text"
        value={value}
        required={required}
        placeholder={placeholder}
        autoComplete="off"
        onChange={(e) => {
          saisieUtilisateur.current = true;
          setResolue(false);
          onChange(e.target.value);
        }}
        onFocus={() => setOuvert(suggestions.length > 0)}
      />
      {ouvert && suggestions.length > 0 && (
        <ul className="adresse-auto-liste">
          {suggestions.map((s) => (
            <li key={s.placeId}>
              <button type="button" onClick={() => choisir(s)}>
                {s.description}
              </button>
            </li>
          ))}
        </ul>
      )}
      {indisponible && (
        <p className="adresse-auto-note">
          Recherche d&apos;adresse indisponible — saisissez l&apos;adresse à la
          main. La position sur la carte devra être renseignée plus tard.
        </p>
      )}
    </div>
  );
};

export default AddressAutocomplete;
